import jwtAxios, {sseRequest} from '@crema/services/auth/jwt-auth';
import AddDataUI from './AddDataUI';
import {useState, useEffect} from 'react';
import {useIntl} from 'react-intl';
import AppInfoView from '@crema/core/AppInfoView';
import {fetchError, showMessage} from 'redux/actions';
import {useDispatch} from 'react-redux';
import * as XLSX from 'xlsx';

const AddDataGL = () => {
  const dispatch = useDispatch();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const {messages} = useIntl();
  const [doneStatusCount, setDoneStatusCount] = useState(0);
  const [allStatusCount, setAllStatusCount] = useState(0);
  const [errorStatusCount, setErrorStatusCount] = useState(0);
  const [errorData, setErrorData] = useState([]);
  const [fileName, setFileName] = useState('');
  const [validationError, setValidationError] = useState('');
  const [isFileValid, setIsFileValid] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(''); // 'uploading', 'processing', 'completed', 'error'
  const [uploadMessage, setUploadMessage] = useState('');
  const [isUploadCompleted, setIsUploadCompleted] = useState(false);
  const [archiveData, setArchiveData] = useState([]);
  const [uploadFinished, setUploadFinished] = useState(false);

  const validateFileHeaders = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, {type: 'array'});
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {header: 1});

          if (jsonData.length === 0) {
            resolve({valid: false, error: 'File is empty'});
            return;
          }

          const headers = jsonData[0];
          const requiredHeaders = [
            'Full Name',
            'Phone',
            'Age',
            'E-mail Address',
            'Country',
            'marks',
            'MPC_4',
            'data_source',
          ];

          // Normalize headers for comparison
          const normalizeHeader = (header) => {
            return header
              ?.toString()
              .toLowerCase()
              .trim()
              .replace(/[\s\-_]/g, '');
          };

          const headerMappings = {
            fullname: ['fullname', 'full_name', 'name'],
            phone: ['phone', 'phonenumber', 'phone_number'],
            age: ['age'],
            emailaddress: [
              'email',
              'emailaddress',
              'email_address',
              'e-mail',
              'e_mail',
            ],
            country: ['country'],
            marks: ['marks', 'mark'],
            mpc4: ['mpc_4', 'mpc4'],
            datasource: ['datasource', 'data_source', 'datasource'],
          };

          const missingHeaders = requiredHeaders.filter((requiredHeader) => {
            const normalizedRequired = normalizeHeader(requiredHeader);
            const possibleMatches = headerMappings[normalizedRequired] || [
              normalizedRequired,
            ];

            return !headers.some((header) => {
              const normalizedHeader = normalizeHeader(header);
              return possibleMatches.some(
                (match) => normalizedHeader === normalizeHeader(match),
              );
            });
          });

          if (missingHeaders.length > 0) {
            resolve({
              valid: false,
              error: `Missing: ${missingHeaders.join(', ')}`,
            });
          } else {
            resolve({valid: true});
          }
        } catch (error) {
          resolve({valid: false, error: 'Invalid Excel file'});
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFileSelection = async (files) => {
    setUploadedFiles(files);
    setValidationError('');
    setIsFileValid(true);
    setIsClicked(false);
    setDoneStatusCount(0);
    setAllStatusCount(0);
    setErrorStatusCount(0);
    setErrorData([]);
    setUploadProgress(0);
    setUploadStatus('');
    setUploadMessage('');
    setIsUploadCompleted(false); // Reset upload completed state when file changes

    // Validate file immediately if one is selected
    if (files && files.length > 0) {
      const validation = await validateFileHeaders(files[0]);
      if (!validation.valid) {
        setValidationError(validation.error);
        setIsFileValid(false);
      }
    }
  };

  const sendFile = async () => {
    console.log();
    if (!uploadedFiles || !uploadedFiles.length) {
      dispatch(fetchError('one file only'));
      return;
    }
    if (uploadedFiles.length > 1) {
      dispatch(fetchError('one file only'));
      return;
    }
    const fileExtention = uploadedFiles[0].name.split('.')[1];
    setFileName(uploadedFiles[0].name.split('.')[0]);
    if (
      !(
        fileExtention.includes('xlsx') ||
        fileExtention.includes('xlsm') ||
        fileExtention.includes('xlsb') ||
        fileExtention.includes('xltx') ||
        fileExtention.includes('xltm') ||
        fileExtention.includes('xls') ||
        fileExtention.includes('xlt') ||
        fileExtention.includes('xls') ||
        fileExtention.includes('xml') ||
        fileExtention.includes('xml') ||
        fileExtention.includes('xlam') ||
        fileExtention.includes('xla') ||
        fileExtention.includes('xlw') ||
        fileExtention.includes('xlr')
      )
    ) {
      dispatch(fetchError('this file not supported'));
      return;
    }
    // create a new FormData object and append the file to it
    const formData = new FormData();
    formData.append('myfile', uploadedFiles[0]);
    formData.append('fileName', fileName);

    // Reset and set initial states
    setIsLoading(true);
    setIsClicked(true);
    setUploadProgress(0);
    setUploadStatus('uploading');
    setUploadMessage('📤 Uploading file...');
    setDoneStatusCount(0);
    setErrorStatusCount(0);
    setAllStatusCount(0);
    setErrorData([]);

    const baseUrl = jwtAxios.defaults.baseURL;
    sseRequest('data/upload-file', {
      body: formData,
      baseUrl: baseUrl, // Pass the backend URL here
      onmessage: (message) => {
        console.log(message);

        if (message.event.includes('StartUploadingFile')) {
          setUploadStatus('uploading');
          setUploadMessage('📤 Uploading file...');
          setUploadProgress(20);
        } else if (message.event.includes('FileDone')) {
          setUploadStatus('processing');
          setUploadMessage('⚙️ Processing data...');
          setUploadProgress(50);
        } else if (message.event.includes('StartUploadingData')) {
          setUploadStatus('processing');
          setUploadMessage('💾 Saving to database...');
          setUploadProgress(70);
        } else if (message.event.includes('doneUploadData')) {
          const doneData = JSON.parse(message.data);

          if (
            doneData.status === 'duplicate_sent' ||
            doneData.status === 'duplicate_free'
          ) {
            // Duplicate saved - treat as error
            setErrorStatusCount((prev) => prev + 1);
            setAllStatusCount((prev) => prev + 1);
            const duplicateData = {
              ...JSON.parse(doneData.data),
              error: 'Duplicate entry',
            };
            setErrorData((prev) => {
              prev.push(duplicateData);
              return prev;
            });
            // Add to archive data
            setArchiveData((prev) => {
              const {id} = duplicateData;
              return {...prev, [id]: {id, status: 'Duplicate entry'}};
            });
            setUploadMessage(`⚠️ ${errorStatusCount + 1} records had errors`);
          } else {
            // Regular success
            setAllStatusCount((prev) => prev + 1);
            setDoneStatusCount((prev) => prev + 1);
            setUploadProgress(90);
            setUploadMessage(
              `✅ Processed ${doneStatusCount + 1} records successfully`,
            );
            // Add to archive data
            setArchiveData((prev) => {
              const successData = JSON.parse(doneData.data);
              const {id} = successData;
              return {...prev, [id]: {id, status: 'Success'}};
            });
          }
        } else if (message.event.includes('errorUploadData')) {
          setErrorStatusCount((prev) => prev + 1);
          setAllStatusCount((prev) => prev + 1);
          const errorRecord = remanageDataError(message.data);
          setErrorData((prev) => {
            prev.push(errorRecord);
            return prev;
          });
          // Add to archive data
          setArchiveData((prev) => {
            const {id} = errorRecord;
            return {...prev, [id]: {id, status: errorRecord.error || 'Error'}};
          });
          setUploadMessage(`⚠️ ${errorStatusCount + 1} records had errors`);
        } else if (message.event.includes('skipEmptyRow')) {
          // Handle skipped empty rows
          setUploadMessage(`⏭️ Skipped empty row`);
        } else if (message.event.includes('ValidationError')) {
          const errorData = JSON.parse(message.data);
          setValidationError(errorData.error);
          setIsFileValid(false);
          setIsLoading(false);
          setIsClicked(false);
          setUploadStatus('error');
          setUploadMessage('❌ File validation failed');
          setUploadProgress(0);
        } else if (
          message.event.includes('ArchiveCreated') ||
          message.event.includes('ReportGenerated')
        ) {
          setUploadStatus('completed');
          setUploadMessage('🎉 Upload completed successfully!');
          setUploadProgress(100);
          setIsUploadCompleted(true);
        }
      },
      onclose: () => {
        setIsLoading(false);
        setUploadFinished(true);
        if (uploadStatus !== 'error' && uploadStatus !== 'completed') {
          setUploadStatus('completed');
          setUploadMessage('🎉 Upload completed!');
          setUploadProgress(100);
          setIsUploadCompleted(true);
        }
      },
      onerror: (error) => {
        dispatch(fetchError('failed:' + error?.message));
        setIsLoading(false);
        setUploadStatus('error');
        setUploadMessage('❌ Upload failed');
        setUploadProgress(0);
      },
    });
  };

  const remanageDataError = (string) => {
    const jsonParsed = JSON.parse(string); // parses entire string to object

    let data;

    if (typeof jsonParsed.data === 'string') {
      try {
        data = JSON.parse(jsonParsed.data); // parses string if needed
      } catch {
        data = jsonParsed.data;
      }
    } else {
      data = jsonParsed.data; // already an object
    }

    // attach error info safely
    data.error = jsonParsed.error || jsonParsed.originalErrorData || null;

    return data;
  };

  // Archive data call function
  const archiveDataCall = () => {
    const archiveEndpoint = '/archive/add-dataBase-Archive';
    // Map archiveData to the required format with "id" and "status"
    const mappedArchiveData = Object.values(archiveData).map((entry) => ({
      id: entry.id,
      status: entry.status,
    }));

    const archivePayload = {
      archive: mappedArchiveData,
      name: fileName,
    };

    console.log('Archive payload:', archivePayload);

    jwtAxios
      .post(archiveEndpoint, archivePayload)
      .then(() => {
        dispatch(showMessage('Data archived successfully'));
        setArchiveData([]);
      })
      .catch((error) => {
        dispatch(fetchError('Failed to archive data: ' + error.message));
      });
  };

  // Effect to call archive when upload finishes
  useEffect(() => {
    if (uploadFinished && Object.keys(archiveData).length > 0) {
      console.log('Upload finished, calling archive...');
      archiveDataCall();
      setUploadFinished(false);
    }
  }, [uploadFinished, archiveData]);

  return (
    <>
      <AppInfoView />
      <AddDataUI
        fileName={fileName}
        isClicked={isClicked}
        allStatusCount={allStatusCount}
        sendDataLoading={isLoading}
        setUploadedFiles={handleFileSelection}
        functionToRun={sendFile}
        messages={messages}
        doneStatusCount={doneStatusCount}
        errorStatusCount={errorStatusCount}
        uploadedFiles={uploadedFiles}
        errorsRows={errorData}
        validationError={validationError}
        isFileValid={isFileValid}
        uploadProgress={uploadProgress}
        uploadStatus={uploadStatus}
        uploadMessage={uploadMessage}
        isUploadCompleted={isUploadCompleted}
      />
    </>
  );
};
export default AddDataGL;
